import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  PropertyService,
} from "@propertelligent/client-api";
import { useForm, FormProvider, Controller } from "react-hook-form";

import Card from "../card";
import InputField from "../form-fields/InputField";
import Uploader from "../CustomUploader/Uploader";
import Radio from "../form-fields/radio";
import AddressAutocompleteRHF from "../AutoComplete/AddressAutocompleteRHF";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import apiHelper from "../../utils/apiHelper";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import RadioGroup from "../form-fields/RadioGroup";
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";

interface ProprtyFormValues {
  propertyType: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  unitImage: File | null;
}

interface EditPropertyProps {
  propertyId?: number;
}

const EditProperty = ({ propertyId: propId }: EditPropertyProps) => {
  const params = useParams<{ propertyId?: string }>();
  const propertyId = propId ?? (params.propertyId ? Number(params.propertyId) : undefined);
  const methods = useForm<ProprtyFormValues>({
    defaultValues: {
      propertyType: "2",
      address: "",
      city: "",
      state: "",
      zip: "",
      unitImage: null,
    },
  });

  const { formState: { isDirty } } = methods;

  const { handleSubmit, control, watch, setValue, formState } = methods;
  const { errors } = formState;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<number | null>(null); // Explicitly type documentId as number | null
  useEffect(() => {
    if (propertyId) {
      getPropertyDetails();
    }
  }, [propertyId]);

  const getPropertyDetails = async () => {
    try {
      const res = await apiHelper(
        PropertyService.getApiProperty1,
        { showNotification: false },
        propertyId
      );

      if (res?.data?.success) {
        const { propertyTypeId, mainImageBase64, streetAddress } = res.data.success;
        setValue("propertyType", propertyTypeId?.toString() || "2");
        setMainImage(res?.data?.success?.mainImageBase64 ? "data:image/*;base64," + res?.data?.success?.mainImageBase64 : null);
        setDocumentId(res?.data?.success?.mainImageId ?? null); // Set documentId or null if not present
        setValue("address", res?.data?.success?.streetAddress || "");
        setValue("city", res?.data?.success?.city || "");
        setValue("state", res?.data?.success?.state || "");
        setValue("zip", res?.data?.success?.zip || "");
      }
    } catch (err) {
      console.error("Failed to fetch property details", err);
    }
  };

  const handleCancelAction = () => {
    setIsUnsavedChangesOpen(false);
  };

  const handleContinueWithoutSaving = () => {
    setIsUnsavedChangesOpen(false);
    navigate(-1);
  };

  const onSubmit = async (data: ProprtyFormValues) => {
    try {
      setLoading(true);

      const propertyObject = {
        id: propertyId,
        address: {
          streetAddress: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
        },
        propertyTypeId: Number(data.propertyType),
      };

      // Update property details
      const response = await apiHelper(PropertyService.putApiProperty,
        {
          showNotification: true,
          successMessage: "Property Updated Successfully",
        },
        propertyId,
        propertyObject
      );

      if (response?.data?.success) {
        // Handle image upload if a new image is provided
        if (data.unitImage) {
          const imgData = {
            EntityTypeId: DocumentEntityTypesEnum.Property,
            EntityId: propertyId,
            DocumentTypeId: DocumentTypesEnum.BeforeImage,
            IsMain: true,
            Document: data.unitImage,
            FileName: data.unitImage.name,
          };

          await apiHelper(
            DocumentService.postApiDocument,
            {
              successMessage: "Property Image Uploaded Successfully",
              showNotification: true,
            },
            imgData
          );
        }
        // Handle image deletion if image is removed and documentId exists
        else if (!data.unitImage && documentId) {
          await apiHelper(
            DocumentService.deleteApiDocument,
            {
              successMessage: "Property Image Deleted Successfully",
              showNotification: true,
            },
            documentId
          );
          setDocumentId(null); // Clear documentId after deletion
          setMainImage(null); // Clear mainImage after deletion
        }

        navigate(-1);
      } else if (response?.errors?.Address) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Property Already Exists",
          type: NotificationType.Failure,
        });
      }
    } catch (error) {
      console.error("Submission failed", error);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to update property",
        type: NotificationType.Failure,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-column gap-1 full-page-content edit-property-page">
        <LeftArrowTopHeader className="mt-2" title="Edit Property" onCancel={() => { if (isDirty) setIsUnsavedChangesOpen(true); else navigate(-1); }} />
        <Card className="mt-0">
          <div className="row">
            <div className="col-md-6">
              <div className="add-property-form-inner">
                <FormProvider {...methods}>
                  <form id="unitForm" onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      <div className="col-md-12 d-flex align-items-center gap-3">
                        <RadioGroup
                          label="Property Type"
                          required
                          requiredMessage="Please select property type"
                        >
                          <Radio
                            name="propertyType"
                            value="1"
                            label="Single Family"
                          />
                          <Radio
                            name="propertyType"
                            value="2"
                            label="Multi Family"
                          />
                        </RadioGroup>
                      </div>


                      <div className="col-md-12">
                        <Controller
                          name="unitImage"
                          control={control}
                          render={() => (
                            <Uploader
                              name="unitImage"
                              allowedFormats={["image/jpeg", "image/png"]}
                              maxSizeMB={2}
                              defaultImage={mainImage}
                            />
                          )}
                        />
                        {errors.unitImage && (
                          <span className="text-danger">{errors.unitImage.message}</span>
                        )}
                      </div>

                      {/* <div className="col-md-12">
                        <AddressAutocompleteRHF />
                      </div> */}
                      <div className="col-md-12">
                        <InputField
                          name="address"
                          label="Street Address"
                          required
                          requiredMessage="Street Address is required"
                          placeholder="Enter Address"
                        />
                      </div>

                      <div className="col-md-4">
                        <InputField
                          name="city"
                          label="City"
                          required
                          requiredMessage="City is required"
                          placeholder="Enter City"
                          validation={{
                            pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: "City must contain only letters",
                            },
                          }}
                        />
                      </div>

                      <div className="col-md-4">
                        <InputField
                          name="state"
                          label="State"
                          required
                          placeholder="Enter State"
                          requiredMessage="State is required"
                          validation={{
                            pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: "State must contain only letters",
                            },
                          }}
                        />
                      </div>

                      <div className="col-md-4">
                        <InputField
                          name="zip"
                          label="Zip Code"
                          required
                          placeholder="Enter Zip Code"
                          requiredMessage="Zip Code is required"
                          validation={{
                            pattern: {
                              value: /^\d{5}$/,
                              message: "Zip code must be 5 digits",
                            },
                          }}
                        />
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <FixedFooter
        onCancel={() => isDirty ? setIsUnsavedChangesOpen(true) : navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        loading={loading}
        buttonLabel={"Save Changes"}
      />
      <ConfirmationPopup
        isOpen={isUnsavedChangesOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you go back or exit this step, your changes will be lost. Do you want to continue?"
        primaryButtonLabel="Cancel"
        secondaryButtonLabel="Continue Without Saving"
        onPrimaryAction={handleCancelAction}
        onSecondaryAction={handleContinueWithoutSaving}
        onClose={handleCancelAction}
        isPrimaryAction={false}
      />
    </>
  );
};

export default EditProperty;